import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../AuthContext";
import { supabase } from "../supabaseClient";

const sampleSongs = [
  { id: "sample-ancient", title: "Ancient of Days", song_key: "C", tags: ["Call to worship", "Upbeat"], notes: "" },
  { id: "sample-fount", title: "Come Thou Fount", song_key: "D", tags: ["Hymn", "Opening"], notes: "" },
  { id: "sample-mercy", title: "His Mercy Is More", song_key: "G", tags: ["Gospel", "Band"], notes: "" },
  { id: "sample-deep", title: "How Deep the Father's Love", song_key: "E", tags: ["Reflection"], notes: "" },
  { id: "sample-christ", title: "In Christ Alone", song_key: "D", tags: ["Core song", "Band"], notes: "" },
  { id: "sample-need", title: "Lord I Need You", song_key: "G", tags: ["Prayer"], notes: "" },
  { id: "sample-praise", title: "O Praise the Name", song_key: "A", tags: ["Closing"], notes: "" },
  { id: "sample-holy", title: "Only a Holy God", song_key: "E", tags: ["Adoration"], notes: "" },
  { id: "sample-yet", title: "Yet Not I But Through Christ in Me", song_key: "C", tags: ["Core song"], notes: "" },
];

const sampleSets = [
  {
    id: "sample-2026-08-21",
    service_date: "2026-08-21",
    leader: "Team lead",
    notes: "Keep transitions simple. Send charts by Wednesday night.",
  },
  {
    id: "sample-2026-08-14",
    service_date: "2026-08-14",
    leader: "Team lead",
    notes: "Acoustic-centered week. Keys are flexible if needed.",
  },
];

const sampleSetSongs = [
  { id: "sample-set-1", set_id: "sample-2026-08-21", title: "Come Thou Fount", song_key: "D", note: "Start with verse 1, full band on verse 2.", position: 0 },
  { id: "sample-set-2", set_id: "sample-2026-08-21", title: "Only a Holy God", song_key: "E", note: "Use this as the main teaching song.", position: 1 },
  { id: "sample-set-3", set_id: "sample-2026-08-21", title: "His Mercy Is More", song_key: "G", note: "Close with a lighter tempo.", position: 2 },
  { id: "sample-set-4", set_id: "sample-2026-08-14", title: "Ancient of Days", song_key: "C", note: "Medium tempo.", position: 0 },
  { id: "sample-set-5", set_id: "sample-2026-08-14", title: "How Deep the Father's Love", song_key: "E", note: "Quiet after the message.", position: 1 },
  { id: "sample-set-6", set_id: "sample-2026-08-14", title: "In Christ Alone", song_key: "D", note: "Strong ending.", position: 2 },
];

const songKeyOptions = [
  "C",
  "C#/Db",
  "D",
  "Eb",
  "E",
  "F",
  "F#/Gb",
  "G",
  "Ab",
  "A",
  "Bb",
  "B",
];
const customKeyValue = "__custom_key__";

function cleanFileName(name) {
  return (
    name
      .toLowerCase()
      .replace(/\.pdf$/i, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "") || "chart"
  );
}

function prefersPdfViewerWindow() {
  const userAgent = navigator.userAgent || "";

  return /Android|iPhone|iPad|iPod/i.test(userAgent);
}

function openPdfUrl(url, viewerWindow = null) {
  if (viewerWindow && !viewerWindow.closed) {
    viewerWindow.location.href = url;
    return;
  }

  if (prefersPdfViewerWindow()) {
    window.location.href = url;
    return;
  }

  window.open(url, "_blank", "noopener,noreferrer");
}

function formatTeamDate(date) {
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(new Date(`${date}T00:00:00`));
}

async function getFunctionErrorMessage(error) {
  if (error?.context instanceof Response) {
    try {
      const body = await error.context.json();

      if (body?.error) {
        return body.error;
      }
    } catch {
      try {
        const text = await error.context.text();

        if (text) {
          return text;
        }
      } catch {
        // Fall back to the client error below.
      }
    }
  }

  return error?.message || "Unexpected error.";
}

function normalizeTags(value) {
  return value
    .split(",")
    .map((tag) => tag.trim())
    .filter(Boolean);
}

function isPresetSongKey(songKey) {
  return songKeyOptions.includes(songKey);
}

function PspWorshipTeam() {
  const { user } = useAuth();
  const [songs, setSongs] = useState(sampleSongs);
  const [sets, setSets] = useState(sampleSets);
  const [weeklySetSongs, setWeeklySetSongs] = useState(sampleSetSongs);
  const [selectedSetId, setSelectedSetId] = useState(sampleSets[0].id);
  const [showAllWeeks, setShowAllWeeks] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [copiedInvite, setCopiedInvite] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [accessCode, setAccessCode] = useState("");
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [requestStatus, setRequestStatus] = useState("");
  const [accessRequests, setAccessRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [adminMode, setAdminMode] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [chartFiles, setChartFiles] = useState({});
  const [extraCharts, setExtraCharts] = useState([]);
  const [extraChartFiles, setExtraChartFiles] = useState({});
  const [extraChartTitles, setExtraChartTitles] = useState({});
  const [annotatedSetFile, setAnnotatedSetFile] = useState(null);
  const [annotatedSetFormVersion, setAnnotatedSetFormVersion] = useState(0);
  const [newSongChartFile, setNewSongChartFile] = useState(null);
  const [newSongFormVersion, setNewSongFormVersion] = useState(0);
  const [addingSong, setAddingSong] = useState(false);
  const [expandedSongNotes, setExpandedSongNotes] = useState({});
  const [editingSongId, setEditingSongId] = useState("");
  const [editSong, setEditSong] = useState({
    notes: "",
    song_key: "",
    tags: "",
    title: "",
  });
  const [savingSongId, setSavingSongId] = useState("");
  const [editingSetId, setEditingSetId] = useState("");
  const [editSet, setEditSet] = useState({
    leader: "",
    notes: "",
    service_date: "",
  });
  const [savingSetId, setSavingSetId] = useState("");
  const [deletingSetId, setDeletingSetId] = useState("");
  const [draggingSetSongId, setDraggingSetSongId] = useState("");
  const [savingSetSongOrder, setSavingSetSongOrder] = useState(false);
  const [removingSetSongId, setRemovingSetSongId] = useState("");
  const [removingAnnotatedSetId, setRemovingAnnotatedSetId] = useState("");
  const [uploadingChartId, setUploadingChartId] = useState("");
  const [uploadingExtraChartId, setUploadingExtraChartId] = useState("");
  const [deletingExtraChartId, setDeletingExtraChartId] = useState("");
  const [uploadingAnnotatedSetId, setUploadingAnnotatedSetId] = useState("");
  const [downloadingSetId, setDownloadingSetId] = useState("");
  const [newSong, setNewSong] = useState({
    notes: "",
    song_key: "",
    tags: "",
    title: "",
  });
  const [customNewSongKey, setCustomNewSongKey] = useState("");
  const [customEditSongKey, setCustomEditSongKey] = useState("");
  const [newSet, setNewSet] = useState({
    leader: "",
    notes: "",
    service_date: "",
  });
  const [setSongForm, setSetSongForm] = useState({
    note: "",
    song_id: "",
  });

  const selectedSet = useMemo(
    () => sets.find((set) => set.id === selectedSetId) || sets[0] || null,
    [selectedSetId, sets]
  );

  const selectedSetSongs = useMemo(
    () =>
      selectedSet
        ? weeklySetSongs
            .filter((song) => song.set_id === selectedSet.id)
            .sort((firstSong, secondSong) => firstSong.position - secondSong.position)
        : [],
    [selectedSet, weeklySetSongs]
  );

  const filteredSongs = useMemo(() => {
    const cleanSearch = searchTerm.trim().toLowerCase();

    if (!cleanSearch) {
      return songs;
    }

    return songs.filter((song) => {
      const tags = Array.isArray(song.tags) ? song.tags.join(" ") : "";
      const searchableText = `${song.title} ${song.song_key} ${tags} ${song.notes}`.toLowerCase();

      return searchableText.includes(cleanSearch);
    });
  }, [searchTerm, songs]);

  const extraChartsBySongId = useMemo(
    () =>
      extraCharts.reduce((chartsBySong, chart) => {
        const songCharts = chartsBySong[chart.song_id] || [];

        return {
          ...chartsBySong,
          [chart.song_id]: [...songCharts, chart],
        };
      }, {}),
    [extraCharts]
  );

  const inviteText =
    "Here is this week's PSP Worship Team page: https://worthyforworship.com/psp-worship-team";

  const canManage = isAdmin && adminMode;
  const visibleSets = showAllWeeks ? sets : sets.slice(0, 4);
  const hiddenSetCount = Math.max(sets.length - visibleSets.length, 0);

  useEffect(() => {
    let ignore = false;

    async function checkAccess() {
      const accessState = await loadPspAccessState();

      if (ignore) {
        setCheckingAccess(false);
        return;
      }

      if (accessState.unlocked) {
        await loadPspWorkspace();
      } else {
        setLoading(false);
      }
    }

    checkAccess();

    return () => {
      ignore = true;
    };
    // loadPspAccessState and loadPspWorkspace are local async loaders that read
    // the current user id. Re-run them only when the signed-in user changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  async function loadPspAccessRequests() {
    setLoadingRequests(true);

    const { data, error: requestError } = await supabase.rpc(
      "list_psp_worship_team_access_requests"
    );

    if (requestError) {
      setError(requestError.message);
      setAccessRequests([]);
    } else {
      setAccessRequests(data || []);
    }

    setLoadingRequests(false);
  }

  async function loadPspAccessState() {
    if (!user) {
      setCheckingAccess(false);
      return { error: "", unlocked: false };
    }

    setCheckingAccess(true);

    const [accessResult, adminResult, requestResult] = await Promise.all([
      supabase.rpc("has_psp_worship_team_access"),
      supabase.rpc("is_psp_worship_team_admin", {
        user_id: user.id,
      }),
      supabase.rpc("get_psp_worship_team_access_request_status"),
    ]);

    const accessStateError =
      accessResult.error || adminResult.error || requestResult.error;

    if (accessStateError) {
      setError(accessStateError.message);
      setCheckingAccess(false);
      return { error: accessStateError.message, unlocked: false };
    }

    const nextIsAdmin = Boolean(adminResult.data);
    const nextIsUnlocked = Boolean(accessResult.data);

    setIsAdmin(nextIsAdmin);
    setIsUnlocked(nextIsUnlocked);
    setRequestStatus(requestResult.data || "");
    setCheckingAccess(false);

    if (nextIsAdmin) {
      loadPspAccessRequests();
    }

    return { error: "", unlocked: nextIsUnlocked };
  }

  async function loadPspWorkspace() {
    setLoading(true);
    setError("");

    const [
      memberResult,
      songsResult,
      extraChartsResult,
      setsResult,
      setSongsResult,
    ] = await Promise.all([
      supabase
        .from("psp_worship_team_members")
        .select("role")
        .eq("user_id", user.id)
        .maybeSingle(),
      supabase
        .from("psp_worship_team_songs")
        .select("id, title, song_key, tags, file_path, notes, created_at")
        .order("title", { ascending: true }),
      supabase
        .from("psp_worship_team_song_charts")
        .select("id, song_id, title, file_path, created_at")
        .order("created_at", { ascending: true }),
      supabase
        .from("psp_worship_team_sets")
        .select("id, service_date, leader, annotated_file_path, notes, created_at")
        .order("service_date", { ascending: false }),
      supabase
        .from("psp_worship_team_set_songs")
        .select("id, set_id, song_id, title, song_key, note, position")
        .order("position", { ascending: true }),
    ]);

    if (
      memberResult.error ||
      songsResult.error ||
      setsResult.error ||
      setSongsResult.error
    ) {
      setError(
        "Showing starter content until the PSP Supabase tables are applied and your account has team access."
      );
      setLoading(false);
      return;
    }

    const loadedSets = setsResult.data || [];
    const memberRole = memberResult.data?.role || "";

    if (!memberRole) {
      setError("Your account is signed in, but it has not been added to PSP yet.");
    } else if (extraChartsResult.error) {
      setError("Extra charts will show after the latest PSP migration is applied.");
    }

    setIsAdmin(memberRole === "admin");
    setSongs(songsResult.data || []);
    setExtraCharts(extraChartsResult.error ? [] : extraChartsResult.data || []);
    setSets(loadedSets);
    setWeeklySetSongs(setSongsResult.data || []);
    setSelectedSetId((currentSetId) =>
      loadedSets.some((set) => set.id === currentSetId)
        ? currentSetId
        : loadedSets[0]?.id || ""
    );
    setLoading(false);
  }

  async function copyInviteLink() {
    try {
      await navigator.clipboard.writeText(inviteText);
      setCopiedInvite(true);
      window.setTimeout(() => setCopiedInvite(false), 1800);
    } catch {
      setCopiedInvite(false);
    }
  }

  async function handleAccessSubmit(event) {
    event.preventDefault();
    setError("");
    setMessage("");

    const { data, error: claimError } = await supabase.rpc(
      "claim_psp_worship_team_access",
      {
        access_code: accessCode,
      }
    );

    if (claimError) {
      setError(claimError.message);
      return;
    }

    setAccessCode("");

    if (data) {
      setIsUnlocked(true);
      setMessage("Access unlocked.");
      await loadPspAccessState();
      await loadPspWorkspace();
      return;
    }

    const accessState = await loadPspAccessState();

    if (accessState.unlocked) {
      setMessage("Access unlocked.");
      await loadPspWorkspace();
    } else if (!accessState.error) {
      setError(
        "The password worked, but saved access was not found. Check that the latest Supabase migration has been applied."
      );
    }
  }

  async function requestAccess() {
    setError("");
    setMessage("");

    const { error: requestError } = await supabase.rpc(
      "request_psp_worship_team_access"
    );

    if (requestError) {
      setError(requestError.message);
      return;
    }

    setRequestStatus("pending");
    setMessage("Access request sent.");
  }

  async function reviewAccessRequest(requestId, nextStatus) {
    setError("");
    setMessage("");

    const { error: reviewError } = await supabase.rpc(
      "review_psp_worship_team_access_request",
      {
        request_id: requestId,
        next_status: nextStatus,
      }
    );

    if (reviewError) {
      setError(reviewError.message);
      return;
    }

    setMessage(`Request ${nextStatus}.`);
    loadPspAccessRequests();
  }

  async function addTeamSong(event) {
    event.preventDefault();
    setError("");
    setMessage("");

    if (!isAdmin) {
      setError("Only PSP admins can add songs.");
      return;
    }

    const title = newSong.title.trim();

    if (!title) {
      setError("Add a song title first.");
      return;
    }

    if (newSongChartFile && newSongChartFile.type !== "application/pdf") {
      setError("Please choose a PDF file.");
      return;
    }

    const savedSongKey =
      newSong.song_key === customKeyValue ? customNewSongKey.trim() : newSong.song_key;

    if (!savedSongKey) {
      setError("Choose a key for the song.");
      return;
    }

    setAddingSong(true);

    const { data: insertedSong, error: insertError } = await supabase
      .from("psp_worship_team_songs")
      .insert({
        notes: newSong.notes.trim(),
        song_key: savedSongKey,
        tags: normalizeTags(newSong.tags),
        title,
      })
      .select("id, title")
      .single();

    if (insertError) {
      setError(insertError.message);
      setAddingSong(false);
      return;
    }

    if (newSongChartFile) {
      const formData = new FormData();
      const filePath = `${user.id}/psp-worship-team/charts/${insertedSong.id}-${cleanFileName(
        newSongChartFile.name
      )}.pdf`;

      formData.append("action", "upload");
      formData.append("filePath", filePath);
      formData.append("file", newSongChartFile);

      const { data: uploadData, error: uploadError } = await supabase.functions.invoke(
        "r2-song-files",
        {
          body: formData,
        }
      );

      if (uploadError) {
        setError(
          `Song was added, but the chart PDF did not upload: ${await getFunctionErrorMessage(
            uploadError
          )}`
        );
        setAddingSong(false);
        await loadPspWorkspace();
        return;
      }

      const { error: updateError } = await supabase
        .from("psp_worship_team_songs")
        .update({ file_path: uploadData.filePath })
        .eq("id", insertedSong.id);

      if (updateError) {
        setError(`Song was added, but the chart PDF was not saved: ${updateError.message}`);
        setAddingSong(false);
        await loadPspWorkspace();
        return;
      }
    }

    setNewSong({ notes: "", song_key: "", tags: "", title: "" });
    setCustomNewSongKey("");
    setNewSongChartFile(null);
    setNewSongFormVersion((currentVersion) => currentVersion + 1);
    setAddingSong(false);
    setMessage(
      newSongChartFile
        ? "Song and chart added to the PSP library."
        : "Song added to the PSP library."
    );
    await loadPspWorkspace();
  }

  async function addWeeklySet(event) {
    event.preventDefault();
    setError("");
    setMessage("");

    if (!newSet.service_date) {
      setError("Choose a Friday date first.");
      return;
    }

    const { data, error: insertError } = await supabase
      .from("psp_worship_team_sets")
      .insert({
        leader: newSet.leader.trim(),
        notes: newSet.notes.trim(),
        service_date: newSet.service_date,
      })
      .select("id")
      .single();

    if (insertError) {
      setError(insertError.message);
      return;
    }

    setNewSet({ leader: "", notes: "", service_date: "" });
    setSelectedSetId(data.id);
    setMessage("Weekly set created.");
    await loadPspWorkspace();
  }

  async function addSongToSelectedSet(event) {
    event.preventDefault();
    setError("");
    setMessage("");

    if (!selectedSet) {
      setError("Choose a Friday set first.");
      return;
    }

    const song = songs.find((currentSong) => currentSong.id === setSongForm.song_id);

    if (!song) {
      setError("Choose a song from the PSP library first.");
      return;
    }

    const nextPosition = selectedSetSongs.length;
    const { error: insertError } = await supabase
      .from("psp_worship_team_set_songs")
      .insert({
        note: setSongForm.note.trim(),
        position: nextPosition,
        set_id: selectedSet.id,
        song_id: song.id,
        song_key: song.song_key,
        title: song.title,
      });

    if (insertError) {
      setError(insertError.message);
      return;
    }

    setSetSongForm({ note: "", song_id: "" });
    setMessage(`"${song.title}" added to this Friday set.`);
    await loadPspWorkspace();
  }

  async function removeSongFromSelectedSet(setSong) {
    const shouldRemove = window.confirm(
      `Remove "${setSong.title}" from this week's set?`
    );

    if (!shouldRemove) {
      return;
    }

    setError("");
    setMessage("");

    if (!isAdmin) {
      setError("Only PSP admins can remove songs from weeks.");
      return;
    }

    setRemovingSetSongId(setSong.id);

    const { error: deleteError } = await supabase
      .from("psp_worship_team_set_songs")
      .delete()
      .eq("id", setSong.id);

    if (deleteError) {
      setError(deleteError.message);
      setRemovingSetSongId("");
      return;
    }

    const remainingSongs = selectedSetSongs.filter((song) => song.id !== setSong.id);
    const positionUpdates = remainingSongs.map((song, index) =>
      supabase
        .from("psp_worship_team_set_songs")
        .update({ position: index })
        .eq("id", song.id)
    );
    const updateResults = await Promise.all(positionUpdates);
    const failedUpdate = updateResults.find((result) => result.error);

    if (failedUpdate?.error) {
      setError(failedUpdate.error.message);
      setRemovingSetSongId("");
      return;
    }

    setRemovingSetSongId("");
    setMessage(`"${setSong.title}" removed from this week.`);
    await loadPspWorkspace();
  }

  async function reorderSelectedSetSongs(draggedSongId, targetSongId) {
    if (!draggedSongId || draggedSongId === targetSongId) {
      return;
    }

    if (!isAdmin) {
      setError("Only PSP admins can reorder songs.");
      return;
    }

    const draggedIndex = selectedSetSongs.findIndex((song) => song.id === draggedSongId);
    const targetIndex = selectedSetSongs.findIndex((song) => song.id === targetSongId);

    if (draggedIndex === -1 || targetIndex === -1) {
      return;
    }

    const reorderedSongs = [...selectedSetSongs];
    const [draggedSong] = reorderedSongs.splice(draggedIndex, 1);

    reorderedSongs.splice(targetIndex, 0, draggedSong);
    setSavingSetSongOrder(true);
    setError("");
    setMessage("");

    const updates = await Promise.all(
      reorderedSongs.map((song, index) =>
        supabase
          .from("psp_worship_team_set_songs")
          .update({ position: index })
          .eq("id", song.id)
      )
    );
    const updateError = updates.find((update) => update.error)?.error;

    if (updateError) {
      setError(updateError.message);
    } else {
      setWeeklySetSongs((currentSongs) => {
        const reorderedSongIds = new Set(reorderedSongs.map((song) => song.id));
        const untouchedSongs = currentSongs.filter(
          (song) => !reorderedSongIds.has(song.id)
        );

        return [
          ...untouchedSongs,
          ...reorderedSongs.map((song, index) => ({
            ...song,
            position: index,
          })),
        ];
      });
      setMessage("Song order updated.");
      await loadPspWorkspace();
    }

    setSavingSetSongOrder(false);
  }

  function startEditingSet(set) {
    setEditingSetId(set.id);
    setEditSet({
      leader: set.leader || "",
      notes: set.notes || "",
      service_date: set.service_date || "",
    });
    setError("");
    setMessage("");
  }

  function cancelEditingSet() {
    setEditingSetId("");
    setEditSet({ leader: "", notes: "", service_date: "" });
  }

  async function saveSetEdits(event, set) {
    event.preventDefault();
    setError("");
    setMessage("");

    if (!isAdmin) {
      setError("Only PSP admins can edit weeks.");
      return;
    }

    if (!editSet.service_date) {
      setError("Choose a date for this week.");
      return;
    }

    setSavingSetId(set.id);

    const { error: updateError } = await supabase
      .from("psp_worship_team_sets")
      .update({
        leader: editSet.leader.trim(),
        notes: editSet.notes.trim(),
        service_date: editSet.service_date,
      })
      .eq("id", set.id);

    if (updateError) {
      setError(updateError.message);
      setSavingSetId("");
      return;
    }

    setEditingSetId("");
    setEditSet({ leader: "", notes: "", service_date: "" });
    setSavingSetId("");
    setMessage("Week updated.");
    await loadPspWorkspace();
  }

  async function deleteWeeklySet(set) {
    setError("");
    setMessage("");

    if (!isAdmin) {
      setError("Only PSP admins can delete weeks.");
      return;
    }

    const confirmed = window.confirm(
      `Delete the PSP week for ${formatTeamDate(
        set.service_date
      )}? This will also remove the songs from that week.`
    );

    if (!confirmed) {
      return;
    }

    setDeletingSetId(set.id);

    const { error: deleteError } = await supabase
      .from("psp_worship_team_sets")
      .delete()
      .eq("id", set.id);

    if (deleteError) {
      setError(deleteError.message);
      setDeletingSetId("");
      return;
    }

    setDeletingSetId("");
    setEditingSetId("");
    setEditSet({ leader: "", notes: "", service_date: "" });
    setSelectedSetId("");
    setMessage("Week deleted.");
    await loadPspWorkspace();
  }

  async function openChart(song) {
    if (!song.file_path) {
      setError("This chart does not have a PDF uploaded yet.");
      return;
    }

    setError("");

    const { data, error: signedUrlError } = await supabase.functions.invoke(
      "r2-song-files",
      {
        body: {
          action: "psp-worship-team-chart-signed-url",
          songId: song.id,
        },
      }
    );

    if (signedUrlError) {
      setError(await getFunctionErrorMessage(signedUrlError));
      return;
    }

    window.open(data.signedUrl, "_blank", "noopener,noreferrer");
  }

  async function openAnnotatedSet(set) {
    const viewerWindow = window.open("", "_blank");

    if (!set.annotated_file_path) {
      viewerWindow?.close();
      setError("This week does not have an annotated PDF uploaded yet.");
      return;
    }

    setError("");

    const { data, error: signedUrlError } = await supabase.functions.invoke(
      "r2-song-files",
      {
        body: {
          action: "psp-worship-team-annotated-set-signed-url",
          setId: set.id,
        },
      }
    );

    if (signedUrlError) {
      viewerWindow?.close();
      setError(await getFunctionErrorMessage(signedUrlError));
      return;
    }

    openPdfUrl(data.signedUrl, viewerWindow);
  }

  async function uploadChart(song) {
    const file = chartFiles[song.id];

    setError("");
    setMessage("");

    if (!isAdmin) {
      setError("Only PSP admins can upload charts.");
      return;
    }

    if (!file) {
      setError("Choose a PDF before uploading.");
      return;
    }

    if (file.type !== "application/pdf") {
      setError("Please choose a PDF file.");
      return;
    }

    setUploadingChartId(song.id);

    const formData = new FormData();
    const filePath = `${user.id}/psp-worship-team/charts/${song.id}-${cleanFileName(
      file.name
    )}.pdf`;

    formData.append("action", "upload");
    formData.append("filePath", filePath);
    formData.append("file", file);

    const { data: uploadData, error: uploadError } = await supabase.functions.invoke(
      "r2-song-files",
      {
        body: formData,
      }
    );

    if (uploadError) {
      setError(await getFunctionErrorMessage(uploadError));
      setUploadingChartId("");
      return;
    }

    const { error: updateError } = await supabase
      .from("psp_worship_team_songs")
      .update({ file_path: uploadData.filePath })
      .eq("id", song.id);

    if (updateError) {
      setError(updateError.message);
      setUploadingChartId("");
      return;
    }

    setChartFiles((currentFiles) => {
      const nextFiles = { ...currentFiles };
      delete nextFiles[song.id];
      return nextFiles;
    });
    setUploadingChartId("");
    setMessage(`Chart uploaded for "${song.title}".`);
    await loadPspWorkspace();
  }

  async function openExtraChart(chart) {
    const viewerWindow = window.open("", "_blank");

    setError("");

    const { data, error: signedUrlError } = await supabase.functions.invoke(
      "r2-song-files",
      {
        body: {
          action: "psp-worship-team-extra-chart-signed-url",
          chartId: chart.id,
        },
      }
    );

    if (signedUrlError) {
      viewerWindow?.close();
      setError(await getFunctionErrorMessage(signedUrlError));
      return;
    }

    openPdfUrl(data.signedUrl, viewerWindow);
  }

  async function uploadExtraChart(song) {
    const file = extraChartFiles[song.id];
    const title =
      extraChartTitles[song.id]?.trim() ||
      file?.name?.replace(/\.pdf$/i, "").trim() ||
      "Additional chart";

    setError("");
    setMessage("");

    if (!isAdmin) {
      setError("Only PSP admins can upload extra charts.");
      return;
    }

    if (!file) {
      setError("Choose an extra chart PDF before uploading.");
      return;
    }

    if (file.type !== "application/pdf") {
      setError("Please choose a PDF file.");
      return;
    }

    setUploadingExtraChartId(song.id);

    const formData = new FormData();
    const filePath = `${user.id}/psp-worship-team/extra-charts/${
      song.id
    }-${crypto.randomUUID()}-${cleanFileName(file.name)}.pdf`;

    formData.append("action", "upload");
    formData.append("filePath", filePath);
    formData.append("file", file);

    const { data: uploadData, error: uploadError } = await supabase.functions.invoke(
      "r2-song-files",
      {
        body: formData,
      }
    );

    if (uploadError) {
      setError(await getFunctionErrorMessage(uploadError));
      setUploadingExtraChartId("");
      return;
    }

    const { error: insertError } = await supabase
      .from("psp_worship_team_song_charts")
      .insert({
        file_path: uploadData.filePath,
        song_id: song.id,
        title,
      });

    if (insertError) {
      setError(insertError.message);
      setUploadingExtraChartId("");
      return;
    }

    setExtraChartFiles((currentFiles) => {
      const nextFiles = { ...currentFiles };
      delete nextFiles[song.id];
      return nextFiles;
    });
    setExtraChartTitles((currentTitles) => ({
      ...currentTitles,
      [song.id]: "",
    }));
    setUploadingExtraChartId("");
    setMessage(`Extra chart added for "${song.title}".`);
    await loadPspWorkspace();
  }

  async function deleteExtraChart(chart) {
    const shouldDelete = window.confirm(
      `Remove "${chart.title || "this extra chart"}"?`
    );

    if (!shouldDelete) {
      return;
    }

    setError("");
    setMessage("");
    setDeletingExtraChartId(chart.id);

    const { error: deleteError } = await supabase.functions.invoke("r2-song-files", {
      body: {
        action: "delete-psp-extra-chart-file",
        chartId: chart.id,
      },
    });

    if (deleteError) {
      setError(await getFunctionErrorMessage(deleteError));
      setDeletingExtraChartId("");
      return;
    }

    setDeletingExtraChartId("");
    setMessage("Extra chart removed.");
    await loadPspWorkspace();
  }

  async function uploadAnnotatedSet(set) {
    const file = annotatedSetFile;

    setError("");
    setMessage("");

    if (!isAdmin) {
      setError("Only PSP admins can upload annotated weekly PDFs.");
      return;
    }

    if (!file) {
      setError("Choose an annotated PDF before uploading.");
      return;
    }

    if (file.type !== "application/pdf") {
      setError("Please choose a PDF file.");
      return;
    }

    setUploadingAnnotatedSetId(set.id);

    const formData = new FormData();
    const filePath = `${user.id}/psp-worship-team/annotated-weeks/${set.id}-${cleanFileName(
      file.name
    )}.pdf`;

    formData.append("action", "upload");
    formData.append("filePath", filePath);
    formData.append("file", file);

    const { data: uploadData, error: uploadError } = await supabase.functions.invoke(
      "r2-song-files",
      {
        body: formData,
      }
    );

    if (uploadError) {
      setError(await getFunctionErrorMessage(uploadError));
      setUploadingAnnotatedSetId("");
      return;
    }

    const { error: updateError } = await supabase
      .from("psp_worship_team_sets")
      .update({ annotated_file_path: uploadData.filePath })
      .eq("id", set.id);

    if (updateError) {
      setError(updateError.message);
      setUploadingAnnotatedSetId("");
      return;
    }

    setAnnotatedSetFile(null);
    setAnnotatedSetFormVersion((currentVersion) => currentVersion + 1);
    setUploadingAnnotatedSetId("");
    setMessage(`Annotated PDF uploaded for ${formatTeamDate(set.service_date)}.`);
    await loadPspWorkspace();
  }

  async function removeAnnotatedSet(set) {
    setError("");
    setMessage("");

    if (!isAdmin) {
      setError("Only PSP admins can remove annotated weekly PDFs.");
      return;
    }

    if (!set.annotated_file_path) {
      setError("This week does not have an annotated PDF to remove.");
      return;
    }

    const confirmed = window.confirm(
      `Remove the annotated PDF for ${formatTeamDate(set.service_date)}?`
    );

    if (!confirmed) {
      return;
    }

    setRemovingAnnotatedSetId(set.id);

    const { error: removeError } = await supabase.functions.invoke(
      "r2-song-files",
      {
        body: {
          action: "delete-psp-annotated-set-file",
          setId: set.id,
        },
      }
    );

    if (removeError) {
      setError(await getFunctionErrorMessage(removeError));
      setRemovingAnnotatedSetId("");
      return;
    }

    setAnnotatedSetFile(null);
    setAnnotatedSetFormVersion((currentVersion) => currentVersion + 1);
    setRemovingAnnotatedSetId("");
    setMessage("Annotated PDF removed from this week.");
    await loadPspWorkspace();
  }

  function startEditingSong(song) {
    const isPresetKey = isPresetSongKey(song.song_key);

    setEditingSongId(song.id);
    setEditSong({
      notes: song.notes || "",
      song_key: isPresetKey ? song.song_key : customKeyValue,
      tags: Array.isArray(song.tags) ? song.tags.join(", ") : "",
      title: song.title || "",
    });
    setCustomEditSongKey(isPresetKey ? "" : song.song_key || "");
    setError("");
    setMessage("");
  }

  function cancelEditingSong() {
    setEditingSongId("");
    setEditSong({ notes: "", song_key: "", tags: "", title: "" });
    setCustomEditSongKey("");
  }

  async function saveSongEdits(event, song) {
    event.preventDefault();
    setError("");
    setMessage("");

    if (!isAdmin) {
      setError("Only PSP admins can edit songs.");
      return;
    }

    const title = editSong.title.trim();

    if (!title) {
      setError("Song title cannot be blank.");
      return;
    }

    const savedEditSongKey =
      editSong.song_key === customKeyValue ? customEditSongKey.trim() : editSong.song_key;

    if (!savedEditSongKey) {
      setError("Choose a key for the song.");
      return;
    }

    setSavingSongId(song.id);

    const { error: updateError } = await supabase
      .from("psp_worship_team_songs")
      .update({
        notes: editSong.notes.trim(),
        song_key: savedEditSongKey,
        tags: normalizeTags(editSong.tags),
        title,
      })
      .eq("id", song.id);

    if (updateError) {
      setError(updateError.message);
      setSavingSongId("");
      return;
    }

    setEditingSongId("");
    setEditSong({ notes: "", song_key: "", tags: "", title: "" });
    setCustomEditSongKey("");
    setSavingSongId("");
    setMessage(`"${title}" updated.`);
    await loadPspWorkspace();
  }

  async function downloadWeeklySetPdf(set) {
    const viewerWindow = window.open("", "_blank");

    setDownloadingSetId(set.id);
    setError("");
    setMessage("");

    const { data, error: pdfError } = await supabase.functions.invoke(
      "r2-song-files",
      {
        body: {
          action: "psp-worship-team-set-pdf",
          preferSignedUrl: true,
          setId: set.id,
        },
      }
    );

    if (pdfError) {
      viewerWindow?.close();
      setError(`Could not build the weekly PDF: ${await getFunctionErrorMessage(pdfError)}`);
      setDownloadingSetId("");
      return;
    }

    if (!data?.signedUrl) {
      viewerWindow?.close();
      setError("Could not build the weekly PDF: the server did not return a file.");
      setDownloadingSetId("");
      return;
    }

    openPdfUrl(data.signedUrl, viewerWindow);

    if (data.warnings?.length) {
      const warningNoun = data.warnings.length === 1 ? "chart" : "charts";
      const warningVerb = data.warnings.length === 1 ? "was" : "were";

      setMessage(
        `Weekly PDF opened. ${data.warnings.length} ${warningNoun} ${warningVerb} replaced with note pages.`
      );
    } else {
      setMessage("Weekly set PDF opened.");
    }

    setDownloadingSetId("");
  }

  if (checkingAccess) {
    return (
      <main className="page friday-team-page">
        <p>Checking PSP Worship Team access...</p>
      </main>
    );
  }

  if (!isUnlocked) {
    return (
      <main className="page friday-team-page">
        <section className="friday-team-hero friday-team-lock-hero">
          <div>
            <p className="eyebrow">Team workspace</p>
            <h1>PSP Worship Team</h1>
            <p>Enter the PSP password or request access for your account.</p>
          </div>
        </section>

        <section className="masters-access-panel">
          <form className="form-stack" onSubmit={handleAccessSubmit}>
            <label htmlFor="psp-access-code">Password</label>
            <input
              id="psp-access-code"
              onChange={(event) => setAccessCode(event.target.value)}
              type="password"
              value={accessCode}
            />

            <button className="primary-button" type="submit">
              Unlock PSP Worship Team
            </button>
          </form>

          <div className="masters-request-access">
            <h2>Need access?</h2>
            <p>
              Send a request and a PSP admin can unlock this page for your
              account.
            </p>
            <button
              className="secondary-button"
              disabled={requestStatus === "pending"}
              onClick={requestAccess}
              type="button"
            >
              {requestStatus === "pending" ? "Request pending" : "Request access"}
            </button>
          </div>

          {error && <p className="form-message error">{error}</p>}
          {message && <p className="form-message success">{message}</p>}
        </section>
      </main>
    );
  }

  return (
    <main className="page friday-team-page">
      <section className="friday-team-hero">
        <div>
          <p className="eyebrow">Team workspace</p>
          <h1>PSP Worship Team</h1>
          <p>
            A private place for a small worship team to pick weekly songs, open
            charts, and send one simple link before Friday.
          </p>
        </div>
        <div className="friday-team-share-card">
          <p className="eyebrow">Invite</p>
          <h2>Share the week</h2>
          <p>Copy a link for email or group messages.</p>
          <button className="secondary-button" type="button" onClick={copyInviteLink}>
            {copiedInvite ? "Copied" : "Copy email link"}
          </button>
        </div>
      </section>

      {message && <p className="form-message success">{message}</p>}
      {error && <p className="form-message error">{error}</p>}
      {loading && <p className="form-message">Loading PSP workspace...</p>}

      {isAdmin && (
        <section className="admin-mode-toggle">
          <div>
            <p className="eyebrow">Admin</p>
            <h2>Admin Mode</h2>
          </div>
          <label>
            <input
              checked={adminMode}
              onChange={(event) => setAdminMode(event.target.checked)}
              type="checkbox"
            />
            <span>{adminMode ? "On" : "Off"}</span>
          </label>
        </section>
      )}

      <section className="friday-team-shell" aria-label="PSP worship team tools">
        <aside className="friday-team-sidebar">
          <div className="friday-team-sidebar-heading">
            <h2>Fridays</h2>
            <p>Newest week first.</p>
          </div>

          <div className="friday-week-list">
            {sets.length === 0 ? (
              <p className="song-resource-muted">No weekly sets yet.</p>
            ) : (
              visibleSets.map((set) => (
                <button
                  className={selectedSet?.id === set.id ? "active" : ""}
                  key={set.id}
                  type="button"
                  onClick={() => setSelectedSetId(set.id)}
                >
                  <strong>{formatTeamDate(set.service_date)}</strong>
                  <span>
                    {set.leader ? `Lead: ${set.leader} · ` : ""}
                    {weeklySetSongs.filter((song) => song.set_id === set.id).length} songs
                  </span>
                </button>
              ))
            )}
          </div>
          {sets.length > 4 && (
            <button
              className="week-list-expand-button"
              type="button"
              onClick={() => setShowAllWeeks((currentValue) => !currentValue)}
            >
              {showAllWeeks
                ? "Show fewer weeks"
                : `Show ${hiddenSetCount} older ${hiddenSetCount === 1 ? "week" : "weeks"}`}
            </button>
          )}

          {canManage && (
            <details className="friday-admin-drawer">
              <summary>New week</summary>
              <form className="friday-admin-form" onSubmit={addWeeklySet}>
                <label>
                  Date
                  <input
                    onChange={(event) =>
                      setNewSet((currentSet) => ({
                        ...currentSet,
                        service_date: event.target.value,
                      }))
                    }
                    type="date"
                    value={newSet.service_date}
                  />
                </label>
                <label>
                  Leader
                  <input
                    onChange={(event) =>
                      setNewSet((currentSet) => ({
                        ...currentSet,
                        leader: event.target.value,
                      }))
                    }
                    placeholder="Team lead"
                    type="text"
                    value={newSet.leader}
                  />
                </label>
                <label>
                  Notes
                  <textarea
                    onChange={(event) =>
                      setNewSet((currentSet) => ({
                        ...currentSet,
                        notes: event.target.value,
                      }))
                    }
                    placeholder="Weekly notes"
                    rows="3"
                    value={newSet.notes}
                  />
                </label>
                <button className="primary-button" type="submit">
                  Create week
                </button>
              </form>
            </details>
          )}
        </aside>

        <section className="friday-team-main">
          {selectedSet ? (
            <>
              <div className="friday-set-heading">
                {editingSetId === selectedSet.id ? (
                  <form
                    className="friday-set-edit-form"
                    onSubmit={(event) => saveSetEdits(event, selectedSet)}
                  >
                    <strong>Edit week</strong>
                    <label>
                      Date
                      <input
                        onChange={(event) =>
                          setEditSet((currentSet) => ({
                            ...currentSet,
                            service_date: event.target.value,
                          }))
                        }
                        type="date"
                        value={editSet.service_date}
                      />
                    </label>
                    <label>
                      Lead
                      <input
                        onChange={(event) =>
                          setEditSet((currentSet) => ({
                            ...currentSet,
                            leader: event.target.value,
                          }))
                        }
                        placeholder="Team lead"
                        type="text"
                        value={editSet.leader}
                      />
                    </label>
                    <label>
                      Notes
                      <textarea
                        onChange={(event) =>
                          setEditSet((currentSet) => ({
                            ...currentSet,
                            notes: event.target.value,
                          }))
                        }
                        rows="3"
                        value={editSet.notes}
                      />
                    </label>
                    <div className="friday-set-actions">
                      <button
                        className="primary-button"
                        disabled={savingSetId === selectedSet.id}
                        type="submit"
                      >
                        {savingSetId === selectedSet.id ? "Saving..." : "Save week"}
                      </button>
                      <button
                        className="secondary-button"
                        type="button"
                        onClick={cancelEditingSet}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  <>
                    <div>
                      <p className="eyebrow">This week</p>
                      <h2>{formatTeamDate(selectedSet.service_date)}</h2>
                      <p className="friday-set-lead">
                        Lead: {selectedSet.leader || "TBD"}
                      </p>
                      <p>{selectedSet.notes || "No notes for this week yet."}</p>
                    </div>
                    <div className="friday-set-action-panel">
                      <div className="friday-set-download-actions">
                        <button
                          className="primary-button"
                          disabled={downloadingSetId === selectedSet.id}
                          type="button"
                          onClick={() => downloadWeeklySetPdf(selectedSet)}
                        >
                          {downloadingSetId === selectedSet.id ? "Building PDF..." : "Open weekly PDF"}
                        </button>
                        {selectedSet.annotated_file_path && (
                          <button
                            className="secondary-button"
                            type="button"
                            onClick={() => openAnnotatedSet(selectedSet)}
                          >
                            Open annotated PDF
                          </button>
                        )}
                      </div>
                      {canManage && (
                        <div className="friday-set-actions">
                        <button
                          className="secondary-button"
                          type="button"
                          onClick={() => startEditingSet(selectedSet)}
                        >
                          Edit week
                        </button>
                        <button
                          className="subtle-danger-button"
                          disabled={deletingSetId === selectedSet.id}
                          type="button"
                          onClick={() => deleteWeeklySet(selectedSet)}
                        >
                          {deletingSetId === selectedSet.id ? "Deleting..." : "Delete week"}
                        </button>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>

              {selectedSetSongs.length === 0 ? (
                <div className="library-empty-state">
                  <h3>No songs yet</h3>
                  <p>Add songs from the PSP chart library to build this set.</p>
                </div>
              ) : (
                <ol className="friday-set-list">
                  {selectedSetSongs.map((song, index) => (
                    <li
                      className={`${canManage ? "can-reorder" : ""} ${
                        draggingSetSongId === song.id ? "dragging" : ""
                      }`}
                      draggable={canManage && !savingSetSongOrder}
                      key={song.id}
                      onDragStart={(event) => {
                        if (!canManage) {
                          return;
                        }

                        setDraggingSetSongId(song.id);
                        event.dataTransfer.effectAllowed = "move";
                        event.dataTransfer.setData("text/plain", song.id);
                      }}
                      onDragOver={(event) => {
                        if (!canManage) {
                          return;
                        }

                        event.preventDefault();
                        event.dataTransfer.dropEffect = "move";
                      }}
                      onDrop={(event) => {
                        if (!canManage) {
                          return;
                        }

                        event.preventDefault();
                        const draggedSongId =
                          event.dataTransfer.getData("text/plain") || draggingSetSongId;

                        setDraggingSetSongId("");
                        reorderSelectedSetSongs(draggedSongId, song.id);
                      }}
                      onDragEnd={() => setDraggingSetSongId("")}
                    >
                      <span>{index + 1}</span>
                      {canManage && (
                        <span
                          aria-label="Drag to reorder"
                          className="friday-set-drag-handle"
                          title="Drag to reorder"
                        >
                          <i></i>
                          <i></i>
                          <i></i>
                        </span>
                      )}
                      <div>
                        <strong>{song.title}</strong>
                        <p>{song.note || "No song note."}</p>
                      </div>
                      <em>{song.song_key || "Key?"}</em>
                      {canManage && (
                        <div className="friday-set-song-actions">
                          <button
                            className="subtle-danger-button"
                            disabled={removingSetSongId === song.id}
                            type="button"
                            onClick={() => removeSongFromSelectedSet(song)}
                          >
                            {removingSetSongId === song.id ? "Removing..." : "Remove"}
                          </button>
                        </div>
                      )}
                    </li>
                  ))}
                </ol>
              )}

              {canManage && (
                <details className="friday-admin-drawer friday-main-drawer">
                  <summary>Add song to this week</summary>
                  <form className="friday-admin-form friday-set-song-form" onSubmit={addSongToSelectedSet}>
                    <label>
                      Song
                      <select
                        onChange={(event) =>
                          setSetSongForm((currentForm) => ({
                            ...currentForm,
                            song_id: event.target.value,
                          }))
                        }
                        value={setSongForm.song_id}
                      >
                        <option value="">Choose song</option>
                        {songs.map((song) => (
                          <option key={song.id} value={song.id}>
                            {song.title} ({song.song_key || "No key"})
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Note
                      <input
                        onChange={(event) =>
                          setSetSongForm((currentForm) => ({
                            ...currentForm,
                            note: event.target.value,
                          }))
                        }
                        placeholder="Intro, transition, capo, or band note"
                        type="text"
                        value={setSongForm.note}
                      />
                    </label>
                    <button className="primary-button" type="submit">
                      Add to set
                    </button>
                  </form>
                </details>
              )}

              {canManage && (
                <details className="friday-admin-drawer friday-main-drawer">
                  <summary>Annotated PDF for this week</summary>
                  <form
                    className="friday-admin-form friday-annotated-set-form"
                    key={annotatedSetFormVersion}
                    onSubmit={(event) => {
                      event.preventDefault();
                      uploadAnnotatedSet(selectedSet);
                    }}
                  >
                    <label>
                      Annotated PDF
                      <input
                        accept="application/pdf"
                        onChange={(event) => setAnnotatedSetFile(event.target.files?.[0] || null)}
                        type="file"
                      />
                    </label>
                    <button
                      className="primary-button"
                      disabled={!annotatedSetFile || uploadingAnnotatedSetId === selectedSet.id}
                      type="submit"
                    >
                      {uploadingAnnotatedSetId === selectedSet.id
                        ? "Uploading..."
                        : selectedSet.annotated_file_path
                          ? "Replace annotated PDF"
                          : "Upload annotated PDF"}
                    </button>
                    {selectedSet.annotated_file_path && (
                      <button
                        className="subtle-danger-button"
                        disabled={removingAnnotatedSetId === selectedSet.id}
                        type="button"
                        onClick={() => removeAnnotatedSet(selectedSet)}
                      >
                        {removingAnnotatedSetId === selectedSet.id
                          ? "Removing..."
                          : "Remove annotated PDF"}
                      </button>
                    )}
                  </form>
                </details>
              )}
            </>
          ) : (
            <div className="library-empty-state">
              <h3>No week selected</h3>
              <p>Create or choose a Friday set to begin.</p>
            </div>
          )}
        </section>
      </section>

      <section className="friday-chart-library" aria-label="Shared chart library">
        <div className="friday-section-heading">
          <p className="eyebrow">Shared library</p>
          <h2>Team Charts</h2>
          <p>
            Search the reusable chart pool, open PDFs, and build weekly sets
            from the songs the team already knows.
          </p>
        </div>

        <div className="friday-library-toolbar">
          <label>
            Search songs
            <input
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search title, key, tag, or note"
              type="search"
              value={searchTerm}
            />
          </label>
          <span>
            {filteredSongs.length} {filteredSongs.length === 1 ? "song" : "songs"}
          </span>
        </div>

        {canManage && (
          <details className="friday-admin-drawer friday-main-drawer">
            <summary>Add chart to library</summary>
            <form
              className="friday-admin-form friday-song-form"
              key={newSongFormVersion}
              onSubmit={addTeamSong}
            >
              <label>
                Title
                <input
                  onChange={(event) =>
                    setNewSong((currentSong) => ({
                      ...currentSong,
                      title: event.target.value,
                    }))
                  }
                  placeholder="Come Thou Fount"
                  type="text"
                  value={newSong.title}
                />
              </label>
              <label>
                Key
                <select
                  onChange={(event) =>
                    setNewSong((currentSong) => ({
                      ...currentSong,
                      song_key: event.target.value,
                    }))
                  }
                  value={newSong.song_key}
                >
                  <option value="">Choose key</option>
                  {songKeyOptions.map((keyName) => (
                    <option key={keyName} value={keyName}>
                      {keyName}
                    </option>
                  ))}
                  <option value={customKeyValue}>Custom key</option>
                </select>
              </label>
              {newSong.song_key === customKeyValue && (
                <label>
                  Custom key
                  <input
                    onChange={(event) => setCustomNewSongKey(event.target.value)}
                    placeholder="C-D"
                    type="text"
                    value={customNewSongKey}
                  />
                </label>
              )}
              <label>
                Tags
                <input
                  onChange={(event) =>
                    setNewSong((currentSong) => ({
                      ...currentSong,
                      tags: event.target.value,
                    }))
                  }
                  placeholder="Hymn, Opening"
                  type="text"
                  value={newSong.tags}
                />
              </label>
              <label>
                Chart PDF
                <input
                  accept="application/pdf"
                  onChange={(event) => setNewSongChartFile(event.target.files?.[0] || null)}
                  type="file"
                />
              </label>
              <label className="friday-notes-field">
                Notes
                <textarea
                  onChange={(event) =>
                    setNewSong((currentSong) => ({
                      ...currentSong,
                      notes: event.target.value,
                    }))
                  }
                  placeholder="Optional chart note"
                  rows="3"
                  value={newSong.notes}
                />
              </label>
              <button className="primary-button" disabled={addingSong} type="submit">
                {addingSong ? "Adding..." : "Add song"}
              </button>
            </form>
          </details>
        )}

        {filteredSongs.length === 0 ? (
          <div className="library-empty-state">
            <h3>No songs found</h3>
            <p>Try a different search or add the first PSP chart.</p>
          </div>
        ) : (
          <div className="friday-chart-grid">
            {filteredSongs.map((song) => {
              const songExtraCharts = extraChartsBySongId[song.id] || [];
              const noteIsExpanded = Boolean(expandedSongNotes[song.id]);
              const noteNeedsToggle = (song.notes || "").length > 85;
              const visibleNote =
                noteIsExpanded || !noteNeedsToggle
                  ? song.notes
                  : `${song.notes.slice(0, 85).trim()}...`;

              return (
              <article className="friday-chart-card" key={song.id}>
                {editingSongId === song.id ? (
                  <form
                    className="friday-chart-edit-form"
                    onSubmit={(event) => saveSongEdits(event, song)}
                  >
                    <label>
                      Title
                      <input
                        onChange={(event) =>
                          setEditSong((currentSong) => ({
                            ...currentSong,
                            title: event.target.value,
                          }))
                        }
                        type="text"
                        value={editSong.title}
                      />
                    </label>
                    <label>
                      Key
                      <select
                        onChange={(event) =>
                          setEditSong((currentSong) => ({
                            ...currentSong,
                            song_key: event.target.value,
                          }))
                        }
                        value={editSong.song_key}
                      >
                        <option value="">Choose key</option>
                        {songKeyOptions.map((keyName) => (
                          <option key={keyName} value={keyName}>
                            {keyName}
                          </option>
                        ))}
                        <option value={customKeyValue}>Custom key</option>
                      </select>
                    </label>
                    {editSong.song_key === customKeyValue && (
                      <label>
                        Custom key
                        <input
                          onChange={(event) => setCustomEditSongKey(event.target.value)}
                          placeholder="C-D"
                          type="text"
                          value={customEditSongKey}
                        />
                      </label>
                    )}
                    <label>
                      Tags
                      <input
                        onChange={(event) =>
                          setEditSong((currentSong) => ({
                            ...currentSong,
                            tags: event.target.value,
                          }))
                        }
                        placeholder="Hymn, Opening"
                        type="text"
                        value={editSong.tags}
                      />
                    </label>
                    <label>
                      Notes
                      <textarea
                        onChange={(event) =>
                          setEditSong((currentSong) => ({
                            ...currentSong,
                            notes: event.target.value,
                          }))
                        }
                        rows="3"
                        value={editSong.notes}
                      />
                    </label>
                    <div className="friday-chart-edit-actions">
                      <button
                        className="primary-button"
                        disabled={savingSongId === song.id}
                        type="submit"
                      >
                        {savingSongId === song.id ? "Saving..." : "Save"}
                      </button>
                      <button
                        className="secondary-button"
                        type="button"
                        onClick={cancelEditingSong}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  <>
                    <div className="friday-chart-copy">
                      <h3>{song.title}</h3>
                      <p>Key: {song.song_key || "No key yet"}</p>
                      <p className={song.file_path ? "friday-chart-status ready" : "friday-chart-status"}>
                        {song.file_path ? "PDF ready" : "PDF needed"}
                      </p>
                      {song.notes && (
                        <div className="friday-chart-note">
                          <p>{visibleNote}</p>
                          {noteNeedsToggle && (
                            <button
                              type="button"
                              onClick={() =>
                                setExpandedSongNotes((currentNotes) => ({
                                  ...currentNotes,
                                  [song.id]: !currentNotes[song.id],
                                }))
                              }
                            >
                              {noteIsExpanded ? "Show less" : "Show note"}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="friday-chart-tags">
                      {(Array.isArray(song.tags) ? song.tags : []).map((tag) => (
                        <span key={tag}>{tag}</span>
                      ))}
                    </div>
                  </>
                )}
                <button
                  className="secondary-button friday-chart-open-button"
                  disabled={!song.file_path}
                  type="button"
                  onClick={() => openChart(song)}
                >
                  {song.file_path ? "Open chart" : "Chart coming soon"}
                </button>
                {songExtraCharts.length > 0 && (
                  <div className="friday-extra-charts">
                    <p>Additional charts</p>
                    {songExtraCharts.map((chart) => (
                      <div className="friday-extra-chart-row" key={chart.id}>
                        <button
                          className="secondary-button"
                          type="button"
                          onClick={() => openExtraChart(chart)}
                        >
                          {chart.title || "Extra chart"}
                        </button>
                        {canManage && (
                          <button
                            className="subtle-danger-button"
                            disabled={deletingExtraChartId === chart.id}
                            type="button"
                            onClick={() => deleteExtraChart(chart)}
                          >
                            {deletingExtraChartId === chart.id ? "Removing..." : "Remove"}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {canManage && (
                  <details className="friday-admin-drawer friday-chart-tools">
                    <summary>Song tools</summary>
                    <div className="friday-chart-upload">
                      <button
                        className="secondary-button"
                        type="button"
                        onClick={() => startEditingSong(song)}
                      >
                        Edit details
                      </button>
                      <label>
                        Main chart PDF
                        <input
                          accept="application/pdf"
                          onChange={(event) =>
                            setChartFiles((currentFiles) => ({
                              ...currentFiles,
                              [song.id]: event.target.files?.[0] || null,
                            }))
                          }
                          type="file"
                        />
                      </label>
                      <button
                        className="secondary-button"
                        disabled={!chartFiles[song.id] || uploadingChartId === song.id}
                        type="button"
                        onClick={() => uploadChart(song)}
                      >
                        {uploadingChartId === song.id
                          ? "Uploading..."
                          : song.file_path
                            ? "Replace main PDF"
                            : "Upload main PDF"}
                      </button>
                      <div className="friday-extra-chart-upload">
                        <label>
                          Extra chart name
                          <input
                            onChange={(event) =>
                              setExtraChartTitles((currentTitles) => ({
                                ...currentTitles,
                                [song.id]: event.target.value,
                              }))
                            }
                            placeholder="Capo chart, alternate key..."
                            type="text"
                            value={extraChartTitles[song.id] || ""}
                          />
                        </label>
                        <label>
                          Extra chart PDF
                          <input
                            accept="application/pdf"
                            onChange={(event) =>
                              setExtraChartFiles((currentFiles) => ({
                                ...currentFiles,
                                [song.id]: event.target.files?.[0] || null,
                              }))
                            }
                            type="file"
                          />
                        </label>
                        <button
                          className="secondary-button"
                          disabled={
                            !extraChartFiles[song.id] || uploadingExtraChartId === song.id
                          }
                          type="button"
                          onClick={() => uploadExtraChart(song)}
                        >
                          {uploadingExtraChartId === song.id
                            ? "Uploading..."
                            : "Add extra chart"}
                        </button>
                      </div>
                    </div>
                  </details>
                )}
              </article>
              );
            })}
          </div>
        )}
      </section>

      {isAdmin && adminMode && (
        <section className="masters-admin-panel">
          <details>
            <summary>
              <span>
                <small>Admin</small>
                <strong>Access Requests</strong>
              </span>
              <em>
                {accessRequests.filter((request) => request.status === "pending").length}{" "}
                pending
              </em>
            </summary>

            <div className="masters-admin-content">
              {loadingRequests ? (
                <p className="empty-state">Loading access requests...</p>
              ) : accessRequests.length === 0 ? (
                <p className="empty-state">No access requests yet.</p>
              ) : (
                <ul className="masters-access-request-list">
                  {accessRequests.map((request) => (
                    <li key={request.id}>
                      <div>
                        <strong>
                          {request.display_name || request.email || "Unknown user"}
                        </strong>
                        {request.email && <span>{request.email}</span>}
                        <small>Status: {request.status}</small>
                      </div>

                      {request.status === "pending" && (
                        <div className="row-actions">
                          <button
                            type="button"
                            onClick={() =>
                              reviewAccessRequest(request.id, "approved")
                            }
                          >
                            Approve
                          </button>
                          <button
                            className="subtle-danger-button"
                            type="button"
                            onClick={() =>
                              reviewAccessRequest(request.id, "rejected")
                            }
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </details>
        </section>
      )}
    </main>
  );
}

export default PspWorshipTeam;
